export type InitialRoute =
	| { kind: 'topic'; slug: string }
	| { kind: 'chapter'; slug: string; chapter: number }
	| { kind: 'idea'; slug: string }
	| { kind: 'view'; view: string; tab?: string; socialSub?: 'community' | 'github'; checkoutSuccess?: true }
	| { kind: 'verify-email' }
	| { kind: 'reset-password' };

/** Pure URL-path parser shared by initial mount and popstate navigation. */
export function resolveInitialRoute(pathname: string): InitialRoute | null {
	const path = pathname.replace(/^\/+|\/+$/g, '');
	if (!path) return null;
	const segments = path.split('/').filter(Boolean).map(safeDecodeSegment);
	const first = segments[0];
	// Accept both `/topic/:slug` (canonical) and `/topics/:slug`
	// (legacy) so deep links work regardless of which form the server
	// emits. Chapter links carry the topic slug + chapter number.
	if ((first === 'topic' || first === 'topics') && segments[1]) {
		const chapter = parseChapterRoute(segments);
		if (chapter) return { kind: 'chapter', slug: segments[1], chapter };
		return { kind: 'topic', slug: segments[1] };
	}
	if (first === 'community' && segments[1] === 'ideas' && segments[2]) return { kind: 'idea', slug: segments[2] };
	if (first === 'ai') return { kind: 'view', view: 'ai-create', tab: 'ai-create' };
	if (first === 'community') return { kind: 'view', view: 'community', tab: 'community' };
	if (first === 'github-trending') return { kind: 'view', view: 'github-trending', tab: 'github-trending' };
	if (first === 'social') {
		const sub = segments[1] === 'github' ? 'github' : 'community';
		return { kind: 'view', view: 'social', tab: 'social', socialSub: sub };
	}
	if (first === 'browse') return { kind: 'view', view: 'home-browse', tab: 'home-browse' };
	if (first === 'pricing') return { kind: 'view', view: 'pricing', tab: 'pricing' };
	if (first === 'profile') return { kind: 'view', view: 'profile', tab: 'profile' };
	if (first === 'membership-billing') return { kind: 'view', view: 'membership-billing', tab: 'profile' };
	if (first === 'billing' && segments[1] === 'success' && segments.length === 2) {
		// Creem redirects here after a successful checkout. Land on Profile (not
		// Membership & Billing) and keep the checkoutSuccess flag so the URL is
		// rewritten to /profile?checkout=success by navigationController.
		return { kind: 'view', view: 'profile', tab: 'profile', checkoutSuccess: true };
	}
	if (first === 'history') return { kind: 'view', view: 'history', tab: 'profile' };
	if (first === 'referrals') return { kind: 'view', view: 'referrals', tab: 'profile' };
	if (first === 'messages') return { kind: 'view', view: 'messages', tab: 'profile' };
	if (first === 'redeem') return { kind: 'view', view: 'redeem', tab: 'profile' };
	if (first === 'feedback') return { kind: 'view', view: 'feedback', tab: 'feedback' };
	if (first === 'article') return { kind: 'view', view: 'home-browse', tab: 'browse' };
	if (first === 'verify-email') return { kind: 'verify-email' };
	if (first === 'reset-password') return { kind: 'reset-password' };
	return null;
}

export function preservesPrivateAuthModal(pathname: string): boolean {
	const route = resolveInitialRoute(pathname);
	return route?.kind === 'verify-email' || route?.kind === 'reset-password';
}

function parseChapterRoute(segments: string[]): number | null {
	if (segments[2] !== 'chapter' && segments[2] !== 'chapters') return null;
	const chapter = Number.parseInt(segments[3] ?? '', 10);
	return Number.isInteger(chapter) && chapter > 0 ? chapter : null;
}

function safeDecodeSegment(segment: string): string {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
}
